import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, euroRound, formatApiErrorDetail } from "@/lib/api";
import { trackView } from "@/lib/carViews";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { formatKm } from "@/components/SaleCarCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Fuel, Settings2, Gauge, Calendar, Zap, BadgeCheck, Phone, MessageCircle, Car as CarIcon, ArrowLeft } from "lucide-react";

const Spec = ({ icon: Icon, label, value }) => (
  value ? (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#181A22] p-4" data-testid={`spec-${label}`}>
      <Icon className="w-5 h-5 amber shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-tech">{label}</div>
        <div className="font-semibold">{value}</div>
      </div>
    </div>
  ) : null
);

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [cfg, setCfg] = useState({ phone: "", whatsapp: "" });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    trackView("sale", id);
    Promise.all([api.get(`/sale-cars/${id}`), api.get("/config")])
      .then(([c, g]) => {
        setCar(c.data);
        setCfg(g.data);
        setForm((p) => ({ ...p, message: `Sono interessato a ${c.data.brand} ${c.data.model}${c.data.year ? " (" + c.data.year + ")" : ""}.` }));
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const submit = async () => {
    if (!form.name || !form.phone) { toast.error("Inserisci nome e telefono"); return; }
    setSending(true);
    try {
      await api.post(`/sale-cars/${id}/inquiry`, form);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm((p) => ({ ...p, name: "", phone: "", email: "" }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E63946]" /></div>;
  if (notFound || !car) return (
    <div className="min-h-screen bg-background grain"><Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center">
        <h1 className="font-display text-3xl font-extrabold">Auto non trovata</h1>
        <Button onClick={() => navigate("/vendita")} className="mt-6 bg-[#E63946] hover:bg-[#D62828] text-white rounded-full px-6">Torna alla vendita</Button>
      </div><Footer />
    </div>
  );

  const images = car.images || [];
  const title = `${car.brand} ${car.model}`;
  const waText = encodeURIComponent(`Ciao, sono interessato a ${title}${car.year ? " (" + car.year + ")" : ""} in vendita. È ancora disponibile?`);
  const waNumber = (cfg.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <div className="min-h-screen bg-background grain" data-testid="vehicle-detail-page">
      <Seo
        title={`${title}${car.year ? " " + car.year : ""} usata in vendita a Pontedera — Centro Auto Pontedera`}
        description={(car.description || `${title} in vendita presso Centro Auto Pontedera (PI).`).slice(0, 160)}
        path={`/vendita/${id}`}
        image={images[0]}
      />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-28 sm:pt-32 pb-20">
        <Link to="/vendita" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-sale"><ArrowLeft className="w-4 h-4" /> Tutte le auto in vendita</Link>
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="relative aspect-[16/11] rounded-2xl overflow-hidden bg-black border border-white/10">
              {images.length ? (
                <img src={images[active]} alt={title} className="w-full h-full object-cover" data-testid="gallery-main" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20"><CarIcon className="w-20 h-20" /></div>
              )}
              {car.sold && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><span className="font-display font-extrabold text-3xl text-[#E63946] uppercase tracking-widest -rotate-6 border-4 border-[#E63946] px-6 py-2 rounded">Venduta</span></div>}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-3 flex-wrap" data-testid="gallery-thumbs">
                {images.map((src, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === active ? "border-[#E63946]" : "border-white/10 hover:border-white/30"}`} data-testid={`gallery-thumb-${i}`}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {car.condition && <div className="font-mono-tech text-xs tracking-[0.3em] uppercase amber mb-2">{car.condition}</div>}
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">{title}</h1>
            <div className="mt-4">
              {car.price_on_request || !car.price ? (
                <div className="font-display font-extrabold text-2xl text-[#FF9E00]">Trattativa riservata</div>
              ) : (
                <div className="font-display font-extrabold text-4xl">{euroRound(car.price)}</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <Spec icon={Calendar} label="Anno" value={car.year} />
              <Spec icon={Gauge} label="Chilometri" value={formatKm(car.mileage)} />
              <Spec icon={Fuel} label="Alimentazione" value={car.fuel} />
              <Spec icon={Settings2} label="Cambio" value={car.transmission} />
              <Spec icon={Zap} label="Potenza" value={car.power} />
              <Spec icon={BadgeCheck} label="Stato" value={car.condition} />
            </div>

            {car.description && (
              <div className="mt-6">
                <h3 className="font-mono-tech text-xs tracking-widest uppercase text-muted-foreground mb-2">Descrizione</h3>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{car.description}</p>
              </div>
            )}

            {/* Quick contact */}
            {!car.sold && (
              <div className="flex flex-wrap gap-3 mt-7">
                {waNumber && (
                  <a href={`https://wa.me/${waNumber}?text=${waText}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp">
                    <Button className="bg-[#25D366] hover:bg-[#1eb558] text-white rounded-full px-6 font-semibold gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp</Button>
                  </a>
                )}
                {(cfg.contacts && cfg.contacts.length ? cfg.contacts : (cfg.phone ? [{ name: "", number: cfg.phone }] : [])).map((c, i) => (
                  <a key={i} href={`tel:${c.number}`} data-testid={`contact-call-${i}`}>
                    <Button variant="outline" className="rounded-full border-white/15 bg-transparent px-6 font-semibold gap-2"><Phone className="w-4 h-4" /> Chiama{c.name ? ` ${c.name}` : ""}</Button>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Inquiry form */}
        {!car.sold && (
          <div className="mt-14 max-w-2xl rounded-2xl border border-white/10 bg-[#181A22] p-6 sm:p-8" data-testid="inquiry-form">
            <h2 className="font-display font-extrabold text-2xl uppercase">Richiedi informazioni</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">Lascia i tuoi dati: ti ricontatteremo per questa auto.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs text-muted-foreground">Nome e cognome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="inquiry-name" /></div>
              <div><Label className="text-xs text-muted-foreground">Telefono *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="inquiry-phone" /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-muted-foreground">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="inquiry-email" /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-muted-foreground">Messaggio</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="inquiry-message" /></div>
            </div>
            <Button onClick={submit} disabled={sending} className="mt-5 bg-[#E63946] hover:bg-[#D62828] text-white rounded-full px-8 font-semibold" data-testid="inquiry-submit">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia richiesta"}</Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
